import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import { useCart } from "../context/CartContext";

const Cart = () => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const navigate = useNavigate();

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const { fetchCartItems, setCartCount } = useCart();

  // 1. 장바구니 데이터 로드
  const fetchCart = useCallback(async () => {
    try {
      const items = await fetchCartItems(); // CartContext 사용
      setCartItems(items);
      setSelectedIds(items.map(item => item.id));
    } catch (err) {
      if (err.response?.status === 401) {
        alert("로그인이 필요합니다.");
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  }, [fetchCartItems, navigate]);  

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  // ✅ 재고 사전 확인 후 주문 페이지 이동 함수
  const handleOrder = async (ids) => {
    if (ids.length === 0) {
      alert("주문할 상품을 선택해주세요.");
      return;
    }

    try {
      // Flask의 미리보기 API를 이용해 재고 상태를 먼저 체크합니다.
      const cartIdsParam = ids.join(',');
      const res = await axios.get(`/api/order/selected/preview?cart_ids=${cartIdsParam}`);
      
      // API가 200 OK를 반환하면 재고가 충분한 것이므로 이동합니다.
      navigate('/order/selected', { state: { cartIds: ids } });
      
    } catch (err) {
      // 재고 부족(400) 또는 기타 에러 발생 시 처리
      if (err.response && err.response.data && err.response.data.message) {
        // 서버에서 보낸 구체적인 메시지(예: "재고가 부족합니다: 상품명")를 출력
        alert(err.response.data.message);
      } else {
        alert("주문 처리 준비 중 오류가 발생했습니다.");
      }
      // 에러가 발생했으므로 navigate를 호출하지 않아 장바구니에 그대로 남습니다.
    }
  };

  // 2. 수량 변경 로직 (비동기 DB 업데이트 포함)
  const changeQuantity = async (itemId, delta) => {
    const item = cartItems.find(i => i.id === itemId);
    const newQty = Math.max(1, item.quantity + delta);

    if (newQty === item.quantity) return;

    // 화면 선반영 (Optimistic UI)
    setCartItems(prev => prev.map(i => 
      i.id === itemId ? { ...i, quantity: newQty } : i
    ));

    try {
      await axios.post('/api/cart/update', { item_id: itemId, quantity: newQty });
      const items = await fetchCartItems(); // → count 반영
      setCartItems(items);
    } catch (err) {
      console.error("수량 업데이트 실패", err);
      fetchCart(); // 실패 시 원복
    }
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 3. 개별 삭제
  const removeProduct = async (cartId) => {
    if (!window.confirm("삭제하시겠습니까?")) return;
  
    try {
      const res = await axios.post('/api/cart/remove', {
        cart_id: cartId
      });
  
      if (res.data.success) {
        const items = await fetchCartItems();
        setCartItems(items);
        setSelectedIds(items.map(item => item.id));
      }
    } catch (err) {
      alert("삭제 중 오류가 발생했습니다.");
    }
  };

  // 4. 선택 삭제
  const deleteSelected = async () => {
    if (selectedIds.length === 0) return alert("삭제할 항목을 선택하세요.");
    if (!window.confirm("선택한 항목을 삭제하시겠습니까?")) return;

    try {
      const res = await axios.post('/api/cart/delete/selected', { selected_ids: selectedIds });
      if (res.data.success) {
        const items = await fetchCartItems();
        setCartItems(items);
      }
    } catch (err) {
      alert("삭제 실패");
    }
  };

  // 5. 체크박스 제어
  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(cartItems.map(item => item.id));
    } else {
      setSelectedIds([]);
    }
  };

  // 6. 합계 계산
  const totalPrice = cartItems
  .filter(item => selectedIds.includes(item.id))
  .reduce((sum, item) => {
    const displayPrice = Number(item.display_price || 0);
    const optionTotal = Number(item.option_total || 0);
    const quantity = Number(item.quantity || 0);

    return sum + ((displayPrice + optionTotal) * quantity);
  }, 0);

  if (loading) return <div style={{ padding: '50px', textAlign: 'center' }}>로딩 중...</div>;

  return (
    <div
      style={{
        ...styles.container,
        paddingBottom: isMobile ? '90px' : '30px'
      }}
    >
      <h2 style={{ padding: '0 16px', marginBottom: '20px', marginTop: '30px' }}>장바구니</h2>

      <div style={styles.headerRow}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <input 
            type="checkbox" 
            id="selectAll" 
            checked={selectedIds.length === cartItems.length && cartItems.length > 0}
            onChange={toggleSelectAll}
            style={styles.checkbox}
          />
          <label htmlFor="selectAll" style={{ fontSize: '0.8rem' }}>전체 선택</label>
        </div>
        <button onClick={deleteSelected} style={styles.selectDeleteBtn}>선택 삭제</button>
      </div>

      {cartItems.length === 0 ? (
        <div style={styles.emptyAlert}>장바구니가 비어있습니다.</div>
      ) : (
        <div style={styles.cartWrapper}>
          {cartItems.map(item => (
            <div key={item.id} style={styles.cartItem}>
              <input 
                type="checkbox" 
                checked={selectedIds.includes(item.id)}
                onChange={() => toggleSelect(item.id)}
                style={{ ...styles.checkbox, marginTop: '5px' }}
              />
              
              <img 
                src={`/static/${item.image_path || 'default_thumb.jpg'}`} 
                alt={item.name} 
                style={styles.thumbnail} 
              />

              <div style={styles.cartDetails}>
                <div style={styles.topInfo}>
                  <div style={{ flex: 1 }}>
                    <div style={styles.itemName}>{item.name}</div>

                    {item.options?.length > 0 && (
                      <div style={styles.optionBox}>
                        {item.options.map((opt, idx) => (
                          <div key={idx} style={styles.optionText}>
                            {opt.option_group_name}: {opt.option_item_name}
                            {Number(opt.option_price || 0) > 0 &&
                              ` (+${Number(opt.option_price || 0).toLocaleString()}원)`
                            }
                          </div>
                        ))}
                      </div>
                    )}
                    
                  </div>

                  <button
                    onClick={() => removeProduct(item.id)}
                    style={styles.deleteBtn}
                  >
                    <i className="bi bi-x-circle"></i>
                  </button>
                </div>

                <div style={styles.bottomInfo}>
                  <div style={styles.qtyBox}>
                    <button style={styles.qtyBtn} onClick={() => changeQuantity(item.id, -1)}>−</button>
                    <span style={styles.quantityText}>{item.quantity}</span>
                    <button style={styles.qtyBtn} onClick={() => changeQuantity(item.id, 1)}>＋</button>
                  </div>
                  <div
                    style={{
                      ...styles.subtotal,
                      ...(item.is_sale ? styles.saleSubtotal : null)
                    }}
                  >
                  {item.is_sale ? (
                    <>
                      {(() => {
                        const displayPrice = Number(item.display_price || 0);
                        const originPrice = Number(item.price || 0);
                        const optionTotal = Number(item.option_total || 0);
                        const quantity = Number(item.quantity || 0);

                        const saleTotal = (displayPrice + optionTotal) * quantity;
                        const originTotal = (originPrice + optionTotal) * quantity;

                        return item.is_sale ? (
                          <>
                            <div
                              style={{
                                textDecoration: 'line-through',
                                color: '#999',
                                fontSize: '0.9rem'
                              }}
                            >
                              {originTotal.toLocaleString()}원
                            </div>

                            <div
                              style={{
                                color: '#000',
                                fontWeight: '700'
                              }}
                            >
                              {saleTotal.toLocaleString()}원
                            </div>
                          </>
                        ) : (
                          <div
                            style={{
                              color: '#000',
                              fontWeight: '700'
                            }}
                          >
                            {saleTotal.toLocaleString()}원
                          </div>
                        );
                      })()}
                    </>
                  ) : (
                    <div
                      style={{
                        color: '#000',
                        fontWeight: '700',
                        marginTop: '6px'
                      }}
                    >
                      {(
                        (Number(item.display_price || 0) + Number(item.option_total || 0)) *
                        Number(item.quantity || 0)
                      ).toLocaleString()}원
                    </div>
                  )}
                </div>
                </div>
              </div>
            </div>
          ))}

          {/* 하단 고정 바 */}
          {isMobile ? (
            <div style={styles.fixedBottomBar}>
              <div style={styles.totalText}>
                합계: <span>{totalPrice.toLocaleString()}원</span>
              </div>

              <button
                style={styles.btnPrimary}
                onClick={() => handleOrder(selectedIds)}
              >
                구매하기
              </button>
            </div>
          ) : (
            <div style={styles.bottomAction}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px'
                }}
              >
                <div style={styles.totalText}>
                  합계: <span>{totalPrice.toLocaleString()}원</span>
                </div>
              </div>

              <button
                style={{
                  ...styles.btnPrimary,
                  width: '100%',
                  height: '52px'
                }}
                onClick={() => handleOrder(selectedIds)}
              >
                구매하기
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { maxWidth: '600px', margin: '20px auto', fontFamily: 'sans-serif' },
  headerRow: { display: 'flex', justifyContent: 'space-between', padding: '0 16px', marginBottom: '10px' },
  checkbox: { marginRight: '8px', cursor: 'pointer' },
  selectDeleteBtn: { background: 'none', border: 'none', fontSize: '0.8rem', cursor: 'pointer', color: '#666' },
  emptyAlert: { margin: '20px 16px', padding: '15px', background: '#e9ecef', borderRadius: '4px', fontSize: '0.9rem' },
  cartWrapper: { background: '#fff', padding: '0 16px 100px' },
  cartItem: { display: 'flex', gap: '12px', padding: '20px 0', borderTop: '1px solid #e0e0e0' },
  thumbnail: { width: '64px', height: '64px', objectFit: 'cover', borderRadius: '6px' },
  cartDetails: { flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' },
  topInfo: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  itemName: { fontWeight: '600', fontSize: '0.95rem' },
  optionBox: {
    marginTop: '4px'
  },
  
  optionText: {
    color: '#777',
    fontSize: '12px',
    lineHeight: '1.4'
  },
  deleteBtn: {
    background: 'none',
    border: 'none',
    color: '#ddd',
    fontSize: '1.2rem',
    cursor: 'pointer',
    padding: 0,
    margin: 0,
    lineHeight: 1,
    alignSelf: 'flex-start'
  },
  bottomInfo: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  qtyBox: { display: 'flex', gap: '8px', alignItems: 'center' },
  qtyBtn: { padding: '2px 8px', border: '0px solid #ccc', background: '#f8f8f8', borderRadius: '4px', cursor: 'pointer' },
  quantityText: { minWidth: '20px', textAlign: 'center' },
  subtotal: { fontWeight: '600', fontSize: '0.9rem' },
  fixedBottomBar: {
    position: 'fixed', 
    bottom: 0, 
    left: 0, 
    right: 0, 
    background: '#fff',
    borderTop: '1px solid #e0e0e0', 
    padding: '12px 20px', 
    zIndex: 999, 
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'center'
  },
  bottomAction: {
    margin: '20px 16px 30px'
  },
  totalText: { fontWeight: '700', fontSize: '1.1rem' },
  btnPrimary: { background: '#0d6efd', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer' },
  btnOutline: { background: 'none', border: '1px solid #333', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer' },
  saleSubtotal: {
    marginTop: '-20px'
  }
};

export default Cart;